import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LogIn, UserPlus, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../lib/auth";
import { useAuth } from "../lib/AuthContext";

// Helper to safely stringify FastAPI 422 detail
const formatErr = (detail) => {
  if (!detail) return "Ocorreu um erro. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { setUser, setAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("mode") === "register" ? "register" : "login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    if (mode === "register" && (!name || name.trim().length < 2)) {
      toast.error("Indique o seu nome");
      return;
    }
    setLoading(true);
    try {
      if (mode === "register") {
        const { data } = await axios.post(
          `${API}/auth/register`,
          { name: name.trim(), email: email.trim(), telefone: telefone.trim(), password },
          { withCredentials: true }
        );
        setUser({ user_id: data.user_id, email: data.email, name: data.name, picture: data.picture, telefone: data.telefone });
        toast.success("Conta criada com sucesso!");
        navigate(searchParams.get("next") || "/minha-conta", { replace: true });
      } else {
        const { data } = await axios.post(
          `${API}/auth/login`,
          { email: email.trim(), password },
          { withCredentials: true }
        );
        if (data.role === "admin") {
          setAdmin({ email: data.email });
          toast.success("Bem-vindo, administrador");
          navigate("/admin", { replace: true });
        } else {
          setUser({ user_id: data.user_id, email: data.email, name: data.name, picture: data.picture, telefone: data.telefone });
          toast.success(`Bem-vindo, ${data.name?.split(" ")[0] || "cliente"}`);
          navigate(searchParams.get("next") || "/minha-conta", { replace: true });
        }
      }
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Erro ao autenticar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#0E2452] pt-32 pb-20 px-6 md:px-12" data-testid="login-page">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="section-label mb-2 justify-center">// Área Reservada</div>
          <h1 className="font-display font-bold text-4xl md:text-5xl uppercase text-white leading-tight">
            {mode === "login" ? (
              <>Entrar na <span className="text-[#FF5A00]">sua conta</span></>
            ) : (
              <>Criar <span className="text-[#FF5A00]">conta</span></>
            )}
          </h1>
          <p className="text-white/60 text-sm font-body mt-3">
            Acompanhe os seus pedidos e veja o valor do orçamento em tempo real.
          </p>
        </div>

        <div className="rounded-2xl border border-[#2F568F] bg-[#0B1D44] p-6 md:p-8">
          <form onSubmit={submit} className="flex flex-col gap-3">
            {mode === "register" && (
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/50 font-display block mb-1">Nome</label>
                <input
                  data-testid="reg-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="O seu nome completo"
                  autoComplete="name"
                  className="w-full bg-[#0E2452] border border-[#2F568F] text-white px-4 py-3 rounded-xl focus:outline-none focus:border-[#FF5A00]"
                  required
                />
              </div>
            )}
            {mode === "register" && (
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/50 font-display block mb-1">Telefone</label>
                <input
                  data-testid="reg-phone-input"
                  type="tel"
                  value={telefone}
                  onChange={(e) => setTelefone(e.target.value)}
                  placeholder="+351 9xx xxx xxx"
                  autoComplete="tel"
                  className="w-full bg-[#0E2452] border border-[#2F568F] text-white px-4 py-3 rounded-xl focus:outline-none focus:border-[#FF5A00]"
                />
                <p className="text-[10px] text-white/40 font-body mt-1">Usado apenas para responder aos seus pedidos.</p>
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/50 font-display block mb-1">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@exemplo.pt"
                autoComplete="email"
                className="w-full bg-[#0E2452] border border-[#2F568F] text-white px-4 py-3 rounded-xl focus:outline-none focus:border-[#FF5A00]"
                required
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/50 font-display block mb-1">Palavra-passe</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "register" ? "Mínimo 6 caracteres" : "A sua palavra-passe"}
                autoComplete={mode === "register" ? "new-password" : "current-password"}
                minLength={mode === "register" ? 6 : 1}
                className="w-full bg-[#0E2452] border border-[#2F568F] text-white px-4 py-3 rounded-xl focus:outline-none focus:border-[#FF5A00]"
                required
              />
            </div>

            <button
              data-testid={mode === "login" ? "login-submit" : "register-submit"}
              type="submit"
              disabled={loading}
              className="mt-2 inline-flex items-center justify-center gap-2 bg-[#FF5A00] hover:bg-[#E65100] text-white font-display font-bold uppercase tracking-wider py-3 rounded-full transition-all disabled:opacity-50 active:scale-[0.98]"
            >
              {loading ? "A processar..." : mode === "login" ? "Entrar" : "Criar conta"}
              <ChevronRight className="w-4 h-4" />
            </button>

            {mode === "login" && (
              <div className="text-center">
                <Link
                  to="/forgot-password"
                  data-testid="forgot-link"
                  className="text-[11px] uppercase tracking-widest text-white/50 hover:text-[#FF5A00] font-display font-semibold"
                >
                  Esqueci-me da palavra-passe
                </Link>
              </div>
            )}
          </form>

          <div className="text-center mt-5 text-sm text-white/60 font-body">
            {mode === "login" ? (
              <>
                Ainda não tem conta?{" "}
                <button
                  data-testid="switch-to-register"
                  type="button"
                  onClick={() => setMode("register")}
                  className="text-[#FF5A00] hover:underline inline-flex items-center gap-1"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  Criar conta
                </button>
              </>
            ) : (
              <>
                Já tem conta?{" "}
                <button
                  data-testid="switch-to-login"
                  type="button"
                  onClick={() => setMode("login")}
                  className="text-[#FF5A00] hover:underline inline-flex items-center gap-1"
                >
                  <LogIn className="w-3.5 h-3.5" />
                  Entrar
                </button>
              </>
            )}
          </div>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-white/60 hover:text-[#FF5A00] text-sm font-body">
            ← Voltar ao site
          </Link>
        </div>
      </div>
    </main>
  );
};

export default LoginPage;
